import { useEffect, useRef } from "react";
import { enviroment } from "../../../../enviroment";
import { AuthenticatedTemplate } from "@azure/msal-react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { Role } from "../../../../core/models/entities/Role";
import NavigationBarLink from "./NavigationBarLink";
import { useNavigationBarViewModel } from "../view-models/useNavigationBarViewModel";
import { useProfileContext } from "../../../../contexts/ProfileContext";
import Brand from "../../../components/customer-brand/Brand";
import { LoggedInMenu } from "./LoggedInMenu";
import { WhatsNewMenu } from "./WhatsNewMenu";
import LoadingElement from "../../../components/loading-element/loading-element";
import { AdminMenuDropdown } from "./AdminMenuDropdown";
import { CartNavMenu } from "../../../components/Cart/Cart-Icon/CartNavMenu";
import { useWhiteLabelContext } from "../../../../WhiteLabelContext";
import { linkFormatter } from "../../../../utils/functions/FormatHyperLink";
import { useRepositoriesContext } from "../../../../contexts/RepositoriesContext";
import { getInitialQuery } from "../../../shared/functions/getInitialQuery.function";
import { getAllQuery } from "../../logo-editor/logo-editor.screen";
import { CLARITY_MASK } from "../../../../utils/analytics/clarityAttrs";

// Same rest-delay as the paginator hover-prefetch: a cursor sweeping across
// the nav must not fire heavy list requests.
const HOVER_PREFETCH_DELAY_MS = 400;

export function NavigationBar(): JSX.Element {
  const { userRole, logout, isLoggingOut, pendingOrdersCount } =
    useNavigationBarViewModel();
  const { t } = useTranslation();
  const location = useLocation();

  const { profile } = useProfileContext();
  const { whiteLabel } = useWhiteLabelContext();
  const { designRepository, styleRepository, manufacturerRepository } =
    useRepositoriesContext();

  // Warm the design list's blocking queries when the pointer rests on the
  // Design nav link - immune to cache GC timing, and a plain cache-warm with
  // the exact keys the screen uses (misses just fetch on the screen as today).
  const designHoverTimer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(designHoverTimer.current), []);

  function handleDesignHoverStart() {
    // Already on the design list: a prefetch could refresh the active query
    // mid-view (visible re-sort) - the hop this warms cannot happen anyway.
    if (location.pathname.includes("/design")) return;
    clearTimeout(designHoverTimer.current);
    designHoverTimer.current = setTimeout(() => {
      designRepository.prefetchDesigns(getInitialQuery(new URLSearchParams()));
      styleRepository.prefetchStyles(getAllQuery);
      manufacturerRepository.prefetchManufacturers();
    }, HOVER_PREFETCH_DELAY_MS);
  }

  function handleDesignHoverEnd() {
    clearTimeout(designHoverTimer.current);
  }

  var formattedLink = linkFormatter(whiteLabel?.whiteLabelWebsiteLink);

  // Render
  return (
    <nav className="w-full bg-white border-b border-gray-200 px-8">
      {isLoggingOut && (
        // Full-screen cover from the logout click until the browser has
        // navigated to B2C - hides the cart/profile clears repainting the UI.
        // Same surface as BootScreen (bg-background + LoadingElement without
        // its gray bg): the whole journey out is ONE look - this cover,
        // BootSplash's Logout coverage, the return splash and the signed-out
        // screen. The old white/gray version was the last un-unified loading
        // surface and showed as a gray flash right at the click.
        <div className="fixed inset-0 z-[10000] bg-background flex items-center justify-center">
          <LoadingElement className="!bg-background" />
        </div>
      )}
      <div className="max-w-[90rem] h-7 w-full mx-auto flex justify-between items-center gap-x-10 font-[700] uppercase text-xxs color-[#596675]">
        {/* Clarity masking (design 5.2): the white-label vendor name and the
            contact e-mail/phone in the top strip are on every page. */}
        <span className="flex-1" {...CLARITY_MASK}>
          {whiteLabel?.whiteLabelVendorName ||
            (userRole === Role.Admin ? t("headerFooter.kitmakerAdmin") : "")}
        </span>
        <span className="flex-0">
          <a
            style={{ color: whiteLabel?.colorHex }}
            target="_blank"
            rel="noreferrer noopener"
            href={formattedLink}
          >
            {whiteLabel?.whiteLabelWebsiteLink ||
              (userRole === Role.Admin ? "www." + enviroment.brandWebsite : "")}
          </a>
        </span>
        <span className="flex-1 text-right space-x-4">
          <WhatsNewMenu />
          <span>
            <a
              href={
                "mailto:" + whiteLabel?.whiteLabelContactEmail ||
                (userRole === Role.Admin ? enviroment.brandEmail : "")
              }
              {...CLARITY_MASK}
            >
              {whiteLabel?.whiteLabelContactEmail ||
                (userRole === Role.Admin ? enviroment.brandEmail : "")}
            </a>
          </span>
          <span>
            <a
              href={
                "tel:" + whiteLabel?.whiteLabelContactPhone ||
                (userRole === Role.Admin ? enviroment.brandPhone : "")
              }
              {...CLARITY_MASK}
            >
              {whiteLabel?.whiteLabelContactPhone ||
                (userRole === Role.Admin ? enviroment.brandPhone : "")}
            </a>
          </span>
        </span>
      </div>
      <div className="max-w-[90rem] h-16 w-full mx-auto flex items-center gap-x-10">
        <Brand profile={profile!} />
        <div className="w-full h-full flex justify-between">
          <div className="h-full flex flex-1">
            {userRole !== undefined && (
              <AuthenticatedTemplate>
                {(userRole === Role.Admin ||
                  userRole === Role.Vendor ||
                  userRole === Role.EndUser) && (
                  <NavigationBarLink
                    selected={location.pathname.includes("/logo")}
                    to="/logo"
                  >
                    {t("logo")}
                  </NavigationBarLink>
                )}
                {(userRole === Role.Admin ||
                  userRole === Role.Vendor ||
                  userRole === Role.EndUser) && (
                  <NavigationBarLink
                    selected={location.pathname.includes("/design")}
                    to="/design"
                    onHoverStart={handleDesignHoverStart}
                    onHoverEnd={handleDesignHoverEnd}
                  >
                    {t("design")}
                  </NavigationBarLink>
                )}
                {(userRole === Role.Admin ||
                  userRole === Role.Vendor ||
                  userRole === Role.EndUser) && (
                  <NavigationBarLink
                    selected={location.pathname.includes("/order")}
                    to="/order"
                  >
                    {userRole === Role.Vendor &&
                    pendingOrdersCount &&
                    pendingOrdersCount > 0
                      ? `${t("order")} (${pendingOrdersCount})`
                      : t("order")}
                  </NavigationBarLink>
                )}
                {userRole === Role.Admin && (
                  <NavigationBarLink
                    selected={location.pathname.includes("/vendor")}
                    to="/vendor"
                  >
                    {t("vendor")}
                  </NavigationBarLink>
                )}
                {userRole === Role.Admin && (
                  <NavigationBarLink
                    selected={location.pathname.includes("/manufacturer")}
                    to="/manufacturer"
                  >
                    {t("manufacturer")}
                  </NavigationBarLink>
                )}
                {(userRole === Role.Admin || userRole === Role.Vendor) && (
                  <NavigationBarLink
                    selected={location.pathname.includes("/customer")}
                    to="/customer"
                  >
                    {t("customer")}
                  </NavigationBarLink>
                )}
                {userRole === Role.Admin && (
                  <NavigationBarLink
                    selected={location.pathname.includes("/style")}
                    to="/style"
                  >
                    {t("style")}
                  </NavigationBarLink>
                )}
                {userRole === Role.Admin && <AdminMenuDropdown />}
              </AuthenticatedTemplate>
            )}
          </div>
          <CartNavMenu />
          <LoggedInMenu logout={logout} />
        </div>
      </div>
    </nav>
  );
}
